import { loadCSV } from './csvLoader';
import moment from 'moment';

/**
 * Parse date in DD-MMM-YY format (e.g., "13-Oct-25")
 */
const parseDate = (dateString) => {
  if (!dateString || dateString === '' || dateString === 'Not in used') {
    return null;
  }
  
  // Try parsing DD-MMM-YY format
  const date = moment(dateString, 'DD-MMM-YY', true);
  if (date.isValid()) {
    return date.toISOString();
  }
  
  // Try other formats
  const altDate = moment(dateString);
  if (altDate.isValid()) {
    return altDate.toISOString();
  }
  
  return null;
};

/**
 * Parse service interval string to days and meter intervals
 * Examples: "Every six months", "Three monts", "Every 500 hrs / 45 Days"
 */
const parseServiceInterval = (intervalString) => {
  if (!intervalString || intervalString === '' || intervalString === 'Not in used') {
    return { days: 180, meter: null }; // Default to 6 months
  }
  
  const interval = intervalString.toLowerCase();
  
  // Check for "six months" or "6 months"
  if (interval.includes('six months') || interval.includes('6 months')) {
    return { days: 180, meter: null };
  }
  
  // Check for "three months" or "3 months"
  if (interval.includes('three monts') || interval.includes('three months') || interval.includes('3 months')) {
    return { days: 90, meter: null };
  }
  
  // Check for "Every X hrs / Y Days" format
  const hrsMatch = interval.match(/(\d+)\s*hrs?\s*\/\s*(\d+)\s*days?/i);
  if (hrsMatch) {
    const hours = parseInt(hrsMatch[1]);
    const days = parseInt(hrsMatch[2]);
    return { days: days, meter: hours };
  }
  
  // Check for "Every X days"
  const daysMatch = interval.match(/(\d+)\s*days?/i);
  if (daysMatch) {
    return { days: parseInt(daysMatch[1]), meter: null };
  }
  
  // Default
  return { days: 180, meter: null };
};

/**
 * Map CSV data to equipment format
 */
export const mapScheduleCsvToEquipment = (csvData) => {
  if (!csvData || csvData.length === 0) return [];
  
  return csvData
    .filter(row => {
      // Filter out rows with "Not in used" or empty unit numbers
      const unitNo = row['Unit No'] || row['Unit No.'] || '';
      return unitNo && unitNo !== '' && unitNo !== 'Not in used';
    })
    .map((row, index) => {
      const unitNo = (row['Unit No'] || row['Unit No.'] || '').trim();
      const registration = (row['Regiation Details'] || row['Registration Details'] || '').trim();
      const name = `${unitNo}${registration ? ` - ${registration}` : ''}`;
      
      // Determine meter type - if both KM and HR exist, prefer HR for equipment
      const kmValue = parseFloat(row['KM']) || 0;
      const hrValue = parseFloat(row['HR']) || 0;
      const hasKm = kmValue > 0;
      const hasHr = hrValue > 0;
      
      // Determine which meter to use
      let meterType = 'kms';
      let currentMeterReading = kmValue;
      let lastServiceMeterReading = kmValue; // Will be updated based on last service
      
      // If HR exists and is significant, use hours
      if (hasHr && (!hasKm || hrValue > 100)) {
        meterType = 'hours';
        currentMeterReading = hrValue;
        lastServiceMeterReading = hrValue;
      }
      
      // Parse service interval
      const serviceInterval = parseServiceInterval(row['Service interval'] || row['Service Interval']);
      
      // Parse dates
      const lastServiceDate = parseDate(row['Last service done'] || row['Last Service Done']);
      const nextServiceDue = parseDate(row['Next Service Due'] || row['Next service due']);
      
      // Calculate service interval in meter units if specified
      let serviceIntervalMeter = 10000; // Default
      if (serviceInterval.meter) {
        serviceIntervalMeter = serviceInterval.meter;
      } else if (meterType === 'hours') {
        // For hours-based equipment without specific interval, estimate based on 6 months
        // Assuming 8 hours/day average usage
        serviceIntervalMeter = serviceInterval.days * 8;
      }
      
      // Parse daily usage from CSV
      const dailyUsage = parseFloat(row['Daily Usage'] || row['Daily usage'] || row['DailyUsage'] || 0) || 0;
      
      const equipment = {
        id: `eq_${unitNo.replace(/\s+/g, '_')}_${index}`,
        name: name,
        meterType: meterType,
        currentMeterReading: currentMeterReading,
        serviceIntervalDays: serviceInterval.days,
        serviceIntervalMeter: serviceIntervalMeter,
        dailyUsage: dailyUsage,
        lastServiceDate: lastServiceDate,
        lastServiceMeterReading: lastServiceMeterReading, // Approximate - same as current if no history
        description: (row["Remark's"] || row['Remarks'] || row['Remark'] || '').trim(),
        // Additional fields from CSV
        client: (row['Client'] || '').trim(),
        station: (row['Station'] || '').trim(),
        unitNo: unitNo,
        registration: registration,
        jobCardNo: (row['Job Card No.'] || row['Job Card No'] || '').trim(),
        nextServiceDue: nextServiceDue,
        targetDate: parseDate(row['Target Date'] || row['Target date'] || ''),
        // Asset details (will be populated from Asset_List.csv)
        assetModel: null,
        assetType: null,
        assetSubType: null,
        location: null,
        subLocation: null,
        assetCode: null
      };
      
      return equipment;
    });
};

/**
 * Load schedule data from CSV file
 */
export const loadScheduleCsv = async (filePath = '/Schedule Service Tracker.csv') => {
  try {
    const csvData = await loadCSV(filePath);
    const equipment = mapScheduleCsvToEquipment(csvData);
    return equipment;
  } catch (error) {
    console.error('Error loading schedule CSV:', error);
    throw error;
  }
};

